<template>
  <div data-testid="historical-position">
    <template v-if="isLoading">
      <SkeletonLoader class="historical-position__loader">
        <div
          v-for="index in 6"
          :key="index"
          class="historical-position__row--loader"
        />
      </SkeletonLoader>
    </template>
    <template v-else>
      <Row left-title="Preço" :right-title="position.price.formatted" />
      <Row left-title="Quantidade" :right-title="String(position.quantity)" />
      <Row>
        <template #top-left>
          <div class="historical-position__row__left-container">
            <p title="Rendimento do início">Rendimento do início</p>
            <ButtonIconContextual
              class="historical-position__row__icon-button"
              label="Ver aviso sobre o rendimento do início"
              size="extra-small"
              icon="EF0080"
              @click="openTooltipModal(TooltipModalTypes.ProfitAmount)"
            />
          </div>
        </template>
        <template #top-right>
          <p class="historical-position__row__right-container">
            {{ position.inceptionProfits.amount.formatted }}
          </p>
        </template>
      </Row>
      <Row>
        <template #top-left>
          <div class="historical-position__row__left-container">
            <p title="Rentabilidade do início">Rentabilidade do início</p>
            <ButtonIconContextual
              class="historical-position__row__icon-button"
              label="Ver aviso sobre a rentabilidade do início"
              size="extra-small"
              icon="EF0080"
              @click="openTooltipModal(TooltipModalTypes.ProfitPercentage)"
            />
          </div>
        </template>
        <template #top-right>
          <PercentualText
            class="historical-position__row__right-container"
            :percentual="position.inceptionProfits.percentage"
          />
        </template>
      </Row>
      <Row
        left-title="Proventos recebidos"
        :right-title="paidEventsFormatted"
      />
      <Row
        left-title="Saldo total"
        :right-title="position.currentValue.formatted"
      />
    </template>
    <TooltipModal
      :is-open="isTooltipModalOpen"
      :title="tooltipData.title"
      :description="tooltipData.text"
      @close="closeTooltipModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import {
  Row,
  SkeletonLoader,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { PercentualText } from '@/modules/common/components/PercentualText';
import { TooltipModal } from '@/modules/common/components/TooltipModal';

import { GetInstrumentPositionHistoricalResponse } from '@/modules/trade/services/instruments';
import { tooltipModalData } from './data';
import { TooltipModalTypes } from './types';

interface Props {
  position: GetInstrumentPositionHistoricalResponse;
  isLoading: boolean;
}
const props = defineProps<Props>();

const isTooltipModalOpen = ref(false);
const selectedTooltipType = ref(TooltipModalTypes.ProfitAmount);

const paidEventsFormatted = computed(
  () => props.position.paidEvents?.formatted || '-'
);

const tooltipData = computed(() => {
  return tooltipModalData[selectedTooltipType.value];
});

const openTooltipModal = (type: TooltipModalTypes) => {
  selectedTooltipType.value = type;
  isTooltipModalOpen.value = true;
};

const closeTooltipModal = () => {
  isTooltipModalOpen.value = false;
};
</script>

<style lang="less" scoped>
.historical-position {
  &__loader {
    display: flex;
    flex-direction: column;
  }

  ::v-deep {
    .container-right {
      flex: none;
      margin-left: @size-spacing-x300;
    }
  }

  &__row {
    &__left-container {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;

      p {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
      }
    }

    &__right-container {
      white-space: nowrap;
    }

    &__icon-button {
      margin-left: @size-spacing-x150;
    }

    &--loader {
      height: 29px;
      width: 100%;
      border-radius: @size-radius-x100;
      margin: @size-spacing-x350 0;
    }
  }
}
</style>
